/**
 * finalize.ts (T-14, DS-1g-04 §4.2/§4.3) — AC-1g-05, AC-1g-24.
 *
 * The wire IS the line: the irreversible `ctx.ops.finalize` crosses
 * SERVER-side when the skill's `execute()` accepts the commit-carrying
 * Event. This module issues NO separate finalize call of any kind — grep
 * this file (and its outbox/triage siblings) for a dedicated
 * finalize/commit endpoint call: there is none. Commit is a SIDE EFFECT
 * of the normal dispatcher send path (`markSending` -> server accepts ->
 * `markDelivered`), never a distinct client-initiated action (AC-1g-05;
 * the "no other commit path" structural proof — live-gated on 1l for the
 * server-side half, provable now for the client-side half).
 *
 * The client HOLDS every saved group for an undo grace window
 * (`UNDO_HOLD_MS`, default 60s) before releasing it to the queue —
 * `scheduleRelease` is the ONE place that starts that timer, and its
 * `cancel()` is the ONE way to stop it short (undo tapped, or the toast's
 * owning component unmounts).
 *
 * Undo (AC-1g-24):
 *   - pre-wire (HELD/QUEUED/SENDING-not-yet-DELIVERED): `undoLastSave` is
 *     a pure local IndexedDB write via the state machine's `undoToDraft`.
 *   - post-finalize (DELIVERED): the undo affordance is ABSENT by
 *     construction — the state machine's transition table (T-03) has no
 *     `delivered -> draft` edge, so calling `undoLastSave` on a DELIVERED
 *     record throws `IllegalTransitionError`. That throw IS the negative
 *     probe (AC-1g-24's second side): it is structurally impossible to
 *     route a DELIVERED capture back through this function. The caller
 *     (DailyLog, T-17) is expected to check `canUndo()` first and offer
 *     Correct/Re-route (the T-15 supersede path) instead once it's false.
 *
 * Zero added confirmation dialogs: neither `scheduleRelease`,
 * `releaseNow`, nor `undoLastSave` presents any prompt — Save was one
 * tap, Undo is one tap (UI audit target for BUILD).
 */

import { releaseHeld, undoToDraft } from "../outbox/state-machine";
import type { OutboxRecord, OutboxState } from "../outbox/types";

export const UNDO_HOLD_MS = 60_000;

const UNDOABLE_STATES: OutboxState[] = ["held", "queued", "sending"];

/** AC-1g-24: true on the pre-wire side of the §4.2 line, false once
 * DELIVERED (or REJECTED — a terminal state with no undo either). */
export function canUndo(record: Pick<OutboxRecord, "state">): boolean {
  return UNDOABLE_STATES.includes(record.state);
}

/** Starts the undo grace-window timer. Returns a `cancel()` — the only
 * way to stop the scheduled release; letting the timer run to completion
 * is what actually crosses HELD -> QUEUED (the group becomes eligible
 * for the dispatcher's send attempts, not yet DELIVERED/finalized). */
export function scheduleRelease(capture_id: string, holdMs: number = UNDO_HOLD_MS): { cancel: () => void } {
  const timer = setTimeout(() => {
    void releaseHeld(capture_id);
  }, holdMs);
  return { cancel: () => clearTimeout(timer) };
}

/** Releases a HELD group immediately (e.g. the user dismisses the undo
 * toast early) — the same durable HELD->QUEUED transition
 * `scheduleRelease`'s timer would otherwise perform on its own. */
export async function releaseNow(capture_id: string): Promise<OutboxRecord> {
  return releaseHeld(capture_id);
}

/** Undo-last-save (AC-1g-24). See module doc: throws on a DELIVERED
 * record — that throw is the structural negative probe, not a runtime
 * heuristic this function has to compute itself. */
export async function undoLastSave(capture_id: string): Promise<OutboxRecord> {
  return undoToDraft(capture_id);
}
